import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, orderBy } from 'lodash';

import { HEATMAP_CHANNELS } from 'constants/channels';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { shouldShowTotalActivities } from 'helpers/data';
import { fetchActivities } from 'services/activities/duck';
import { fetchCampaign, fetchCampaignCallouts, fetchActiveChannels } from 'services/campaign/duck';
import { fetchLayerTwoSummary, fetchLayerTwoActivities } from 'services/layerTwo/duck';
import { fetchTimeOfDayHeatMap } from 'services/ttd/duck';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTactics as fetchChannels } from 'services/tactics/duck';
import { setObjectiveShadow } from 'services/config/duck';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { validateStatisticsData } from 'helpers/validate';

// Components and Containers
import { Grid, Row, Col } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import TileWithValidation from 'components/molecules/TileWithValidation';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';

// Call Outs
import CallOutValueWithTreemap from 'components/molecules/CallOutValueWithTreemap';

// Charts
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import GroupedBarChartWithCategories from 'components/molecules/charts/GroupedBarChartWithCategories';
import ChannelDonutChart from 'components/molecules/charts/ChannelDonutChart';
import LayerTwoActivitiesStackedBarChart from 'components/molecules/charts/LayerTwoActivitiesStackedBarChart';
import ChannelStackedChart from 'components/molecules/charts/ChannelStackedChart';
import TimeOfDayHeatMapChart from 'components/molecules/charts/TimeOfDayHeatMapChart';

// Tables
import ActivitiesPivotTable from 'components/molecules/tables/ActivitiesPivotTable';
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import ChannelOverviewTable from 'components/molecules/tables/ChannelOverviewTable';
import ChannelSummaryCallouts from 'components/molecules/ChannelSummaryCallouts';

class LayerTwoSummaryContainer extends Component {
    constructor() {
        super();
        this.state = { entityId: null };
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.match.params.entityId, 10);
        if (entityId !== state.entityId) {
            return {
                ...state,
                entityId
            };
        }

        return null;
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    createPayload(entityId) {
        return {
            transactionId: `entity-${entityId}-summary`,
            entityId
        };
    }

    makeRequestsForData() {
        const entityId = this.state.entityId;
        const payload = this.createPayload(entityId);
        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entityMeta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchLayerTwoSummary(payload);
        this.props.fetchLayerTwoActivities(payload);
        this.props.fetchChannels(payload);
        this.props.fetchCampaign(payload);
        this.props.fetchCampaignCallouts(payload);
        this.props.fetchActivities(payload);
        this.props.fetchTimeOfDayHeatMap(payload);
        this.props.fetchActiveChannels(payload);
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.state.entityId;
        const isTimeRangeChanged = prevProps.selectedTimeRange !== this.props.selectedTimeRange;
        if (isTimeRangeChanged || isEntityChanged) {
            this.makeRequestsForData();
        }
    }

    getCurrentEntityId() {
        return this.state.entityId;
    }

    render() {
        const stub = 'l2-summary';
        const {
            backfillBuckets,
            xAxisLabel,
            enabledChannels,
            entityObjective,
            entityObjectiveLabelCode,
            showTotalActivities,
            showConversionRevenue,
            selectedTimeRange,
            showeCPAonSummaryDashboard,
            showSocialCpconv
        } = this.props;
        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            helpKeySuffix,
            showActivitiesData,
            heatmapTerm,
            singularTerm,
            fullObjectiveTerm,
            pluralTerm,
            primaryActivityField,
            primaryActivityFieldTerm,
            ecpaTerm
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const hasLayerTwoSummaryData = get(this.props, 'layerTwo.entity.summary.hasData', null);
        const hasLayerTwoActivities = get(this.props, 'layerTwo.activities.hasData', null);
        const childSummaryTableData = get(this.props, 'layerTwo.entity.summary.data', []);
        const hasLayerThreeSummaryData = get(this.props, 'layerThreeSummary.hasData', null);
        const hasCalloutData = get(this.props, 'layerThreeSummary.callouts.params.hasData', null);
        const hasActiveChannelsData = get(this.props, 'layerThreeSummary.activeChannels.params.hasData', null);
        const callouts = get(this.props, 'layerThreeSummary.callouts.data', {});
        const calloutWindow = get(this.props, 'layerThreeSummary.callouts.params.timeRanges.previous.windowSize', null);
        const activeChannelData = get(this.props, 'layerThreeSummary.activeChannels.data', {});
        const allChannelsList = get(this.props, 'layerThreeSummary.activeChannels.channels', []);
        const activeChannels = allChannelsList.reduce((prev, channel) => {
            // Active Channel is defined to be a channel with a non-zero imprerssion
            // count for a given time window.
            if (activeChannelData[channel].impressions > 0) {
                prev.push(channel);
            }
            return prev;
        }, []);
        const activeChannelsCount = activeChannels.length;
        const channelOptions = {
            social: { showSocialCpconv }
        };

        const hasLayerThreeChannelsData = get(this.props, 'layerThreeChannels.hasData', null);
        const hasLayerThreeChannelsInsightsData = get(this.props, 'layerThreeChannels.insights', null);

        const hasLayerThreeTimeOfDayData = get(this.props, 'ttd.timeOfDay.hasData', null);
        const timeOfDayData = get(this.props, 'ttd.timeOfDay', {});
        const userId = get(this.props, 'currentUser.id', null);

        let channels = [];
        const channelOverviewTableData = [];
        if (hasLayerThreeChannelsInsightsData) {
            channels = Object.keys(this.props.layerThreeChannels.data);
            // Create Channel Overview Table Data
            channels.forEach((channel) => {
                channelOverviewTableData.push({
                    channel, ...this.props.layerThreeChannels.data[channel].statistics
                });
            });
        }
        const activities = this.props.layerThreeActivities;
        const layerTwoActivities = this.props.layerTwo.activities;
        const hasLayerThreeActivitiesData = get(activities, 'hasData', null);
        const activitiesRenderFlag = hasLayerThreeActivitiesData && (showActivitiesData || showTotalActivities);

        let layerThreeActivitiesData;
        if (hasLayerThreeActivitiesData) {
            layerThreeActivitiesData = Object.entries(activities.data).reduce((prev, entry) => {
                const [name, data] = entry;
                prev.push({ name, ...data.statistics });
                return prev;
            }, []);
        }
        const sortedLayerThreeActivitiesData =
            hasLayerThreeActivitiesData
                ? orderBy(layerThreeActivitiesData, [entityObjective === 'ctr' ? 'total_activities' : `${entityObjective}_activities`], ['desc'])
                : [];

        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');

        const calloutSettings = [
            { key: 'impressions', title: 'Impressions', format: '0,0' },
            {
                key: eCPAActivitiesOrClicks,
                helpKey: `${eCPAActivitiesOrClicks}${helpKeySuffix}`,
                title: eCPAActivitiesOrClicksTerm,
                format: '0,0'
            },
            showeCPAonSummaryDashboard ? {
                key: 'ecpa',
                helpKey: `ecpa${helpKeySuffix}`,
                title: entityObjectiveLabelCode ? `Average ${ecpaTerm}` : 'eCPA',
                format: '$0,0.00'
            } : null,
            {
                component: CallOutValueWithTreemap,
                props: {
                    exportName: 'l2-channels-live',
                    title: 'Channels Live',
                    data: {
                        value: activeChannelsCount,
                        treeMap: activeChannelData,
                        channels: activeChannels,
                        accessor: 'spend'
                    },
                    enabledChannels,
                    format: 'number'
                },
                hasData: hasActiveChannelsData
            }
        ].filter(e => e);

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        // If we have data to render, let us render!
        const hasData = [
            hasLayerTwoSummaryData === null ? true : hasLayerTwoSummaryData,
            hasLayerTwoActivities,
            hasLayerThreeSummaryData,
            hasCalloutData && hasLayerThreeChannelsData,
            hasLayerThreeChannelsData,
            hasLayerThreeChannelsInsightsData,
            hasLayerThreeTimeOfDayData,
            hasLayerThreeActivitiesData
        ].some(d => d);

        const hasHeatmapChannels = channels.some(c => HEATMAP_CHANNELS.indexOf(c) !== -1);

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {hasData && <ExportPDF template={stub} />}
        </React.Fragment>;

        return hasData ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Summary
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard="summary"
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>

                <ChannelSummaryCallouts
                    channelsList={allChannelsList}
                    entityId={this.state.entityId}
                    layer="l2"
                    channelOptions={channelOptions}
                />

                {/* Activities by Child */}
                {hasLayerTwoActivities &&
                    (showActivitiesData || showTotalActivities)
                    && !this.props.enforceSummaryOnlyDashboardView ? (
                        <LayerTwoActivitiesStackedBarChart
                            title={`${primaryActivityFieldTerm.toUpperCase()} BY ${aliasDescriptor}`}
                            exportName={`${stub}-activities`}
                            data={layerTwoActivities.data}
                            keys={layerTwoActivities.keys}
                            rankings={layerTwoActivities.rankings}
                            y={primaryActivityField}
                            xAxisLabel={xAxisLabel}
                            yAxisLabel={primaryActivityFieldTerm}
                            backfillBuckets={backfillBuckets}
                            animateDuration={2000} />
                    ) : (
                        <RenderingTile height='400px' show={hasLayerTwoActivities && (showActivitiesData || showTotalActivities) && !this.props.enforceSummaryOnlyDashboardView} />
                    )}
                {/* Child Tables */}
                {hasLayerTwoSummaryData ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName={`${stub}-table-performance`}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        showeCPA={showeCPAonSummaryDashboard}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['impressions', 'clicks', 'ctr', 'spend'] :
                                ['impressions', 'ecpa_activities', 'spend']
                        }
                        cacheKey={`${stub}-table-performance-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}
                {/* Donut Charts */}
                <Row>
                    <Col md={6}>
                        {hasLayerThreeChannelsInsightsData ? (
                            <ChannelDonutChart
                                exportName="l2-left-donut"
                                title="Impressions by Channel"
                                enabledChannels={enabledChannels}
                                data={get(this.props, 'layerThreeChannels.insights.percentages.impressions', {})}
                                y="impressions"
                                total={get(this.props, 'layerThreeChannels.insights.totals.impressions', 0)}
                                totalTitle="Total Impressions"
                                totalFormat='0,0'
                                animateDuration={2000} />
                        ) : (
                            <RenderingTile height='400px' show={hasLayerThreeChannelsInsightsData} />
                        )}
                    </Col>
                    <Col md={6}>
                        <TileWithValidation
                            hasData={hasLayerThreeChannelsInsightsData}
                            innerComponent={ChannelDonutChart}
                            height='430px'
                            innerProps={{
                                title: `${eCPAActivitiesOrClicksTerm.toUpperCase()} BY CHANNEL`,
                                exportName: 'l2-right-donut',
                                data: get(this.props, `layerThreeChannels.insights.percentages.${eCPAActivitiesOrClicks}`, {}),
                                enabledChannels,
                                total: get(this.props, `layerThreeChannels.insights.totals.${eCPAActivitiesOrClicks}`, 0),
                                totalTitle: eCPAActivitiesOrClicksTerm,
                                totalFormat: '0,0',
                                y: eCPAActivitiesOrClicks,
                                animateDuration: 2000,
                                validation: {
                                    key: eCPAActivitiesOrClicks,
                                    data: get(this.props, 'layerThreeChannels.insights.totals', {}),
                                    validator: validateStatisticsData
                                }
                            }}
                        />
                    </Col>
                </Row>
                {/* Channel Overview Table */}
                {hasLayerThreeChannelsInsightsData ? (
                    <ChannelOverviewTable
                        title="CHANNEL OVERVIEW"
                        exportName={`${stub}-channel-overview`}
                        entityId={this.props.entityMeta.id}
                        data={channelOverviewTableData}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        disableChannelLinks={this.props.enforceSummaryOnlyDashboardView}
                        showeCPA={showeCPAonSummaryDashboard}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['impressions', 'clicks', 'ctr', 'spend'] :
                                undefined
                        }
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelsInsightsData} />
                )}
                {/* TTD Time of Day Chart */}
                {hasHeatmapChannels ? (
                    <React.Fragment>
                        {hasLayerThreeTimeOfDayData ? (
                            <TimeOfDayHeatMapChart
                                title={`${heatmapTerm} BY TIME OF DAY`}
                                exportName={`${stub}-activity-by-time-of-day`}
                                data={timeOfDayData.data}
                                categories={timeOfDayData.categories} />
                        ) : (
                            <RenderingTile height='400px' show={hasLayerThreeTimeOfDayData} />
                        )}
                    </React.Fragment>
                ) : null}
                {/* Activities Chart */}
                {activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView ? (
                    <GroupedBarChartWithCategories
                        title={`TOP ${pluralTerm.toUpperCase()}`}
                        exportName={`${stub}-top-activities`}
                        data={sortedLayerThreeActivitiesData}
                        showTopOnly
                        keys={[
                            entityObjective !== 'ctr' ? { key: `${entityObjective}_activities`, title: fullObjectiveTerm } : null,
                            showTotalActivities ? { key: 'total_activities', title: `Total ${pluralTerm}` } : null
                        ].filter(e => e)}
                        partitionBy="name"
                        filterField={entityObjective !== 'ctr' ? 'total_activities' : primaryActivityField}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='475px' show={activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView} />
                )}

                <TileWithValidation
                    hasData={hasLayerThreeChannelsData}
                    innerComponent={ChannelStackedChart}
                    innerProps={{
                        title: `${eCPAActivitiesOrClicksTerm.toUpperCase()} BY CHANNEL`,
                        exportName: `${stub}-kpi-by-channel`,
                        chart: { type: 'area', zoomType: 'xy' },
                        data: get(this.props, 'layerThreeChannels.groupedData', {}),
                        enabledChannels,
                        y: eCPAActivitiesOrClicks,
                        yAxisLabel: eCPAActivitiesOrClicksTerm,
                        xAxisLabel,
                        backfillBuckets,
                        animateDuration: 2000,
                        validation: {
                            key: eCPAActivitiesOrClicks,
                            data: get(this.props, 'layerThreeChannels.insights.totals', {}),
                            validator: validateStatisticsData
                        }
                    }}
                />

                {activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView ? (
                    <ActivitiesPivotTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN BY CHANNEL`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        objectives={[entityObjective]}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView} />
                )}
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Summary
                </ChannelTitle>
                <Note
                    dashboard="summary"
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='Summary' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entityMeta: state.entities.meta,
    enabledChannels: state.entities.meta.enabledChannels,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    showSocialCpconv: get(state, 'entities.meta.meta.showSocialCpconv', false),
    showeCPAonSummaryDashboard: get(state, 'entities.meta.meta.showeCPAonSummaryDashboard', false),
    enforceSummaryOnlyDashboardView: get(state, 'entities.meta.meta.enforceSummaryOnlyDashboardView', false),
    layerTwo: state.layerTwo,
    ttd: state.ttd,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),

    // LayerThree Services
    // TODO: Refactor to Channels, not Tactics
    layerThreeChannels: state.tactics,
    layerThreeActivities: state.activities,
    // TODO: Rename to 'summary' throughout
    layerThreeSummary: state.campaign
});

export default connect(mapStateToProps, {
    fetchActiveChannels,
    fetchActivities,
    fetchEntityMeta,
    fetchChannels,
    fetchCampaign,
    fetchCampaignCallouts,
    fetchLayerTwoSummary,
    fetchLayerTwoActivities,
    fetchTimeOfDayHeatMap,
    setObjectiveShadow
})(LayerTwoSummaryContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerTwoSummaryContainer.js